import { useTranslation } from '@klv/hooks/useTranslation';

import Icon from '@klv/atoms/icon';
import { convertLocaleForIconName } from '@klv/atoms/icon/utils';
import { LanguageSelector } from '@klv/molecules/language-selector';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { generateClassName } from '@klv/shared/utils/functions';

import { Menu } from './children/menu';

import * as styles from './styles/universalFooter.module.scss';

import type { TIconName } from '@klv/atoms/icon/types';
import type { IUniversalFooter, TBackgroundColor } from './types';


const SOCIAL_PLATFORMS = [
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/Klaviyo/',
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com/klaviyo/',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/klaviyo/',
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/klaviyo/',
  },
] as const;


export const UniversalFooter = ({
  className = '',
  backgroundColor = 'charcoal',
  fullWidth = false,
  langCode,
  menuItems = [],
  copyrightMenuItems = [],
  navigationStyle = 'default',
  siteMetadata,
  sites = [],
  translations = [],
  ...props
}: IUniversalFooter) => {
  
  const isDefaultNav = navigationStyle !== 'minimal';

  const { t, i18n } = useTranslation();
  const { langCode: siteMetadataLangCode = '' } = siteMetadata || {};

  const locale = i18n.language;

  const currentLogos = ['es-ES', 'de-DE', 'fr-FR', 'it-IT'];
  // We only have logos for US, FR, and IT so far. Can update this as we get new ones
  const newFooterLogoForLocale = currentLogos.includes(locale) ? locale : 'en-US';
  const footerIconName: TIconName = `KlaviyoFooterLogo${convertLocaleForIconName(newFooterLogoForLocale)}` as TIconName;

  return (
    <footer className={generateClassName([className, styles.footer, styles[backgroundColor] as TBackgroundColor, navigationStyle === 'minimal' && styles.minimal, fullWidth && styles.fullWidth])} {...props}>
      <div className={generateClassName(['grid', styles.grid])}>
        <div className={styles.beFooter}><div className='be-ix-link-block'></div></div>
        {isDefaultNav && (
          <nav className={styles.footerNav}>
            <ul className={styles.menuContainer}>
              {menuItems?.map((menuItem) => {
                return (
                <li key={menuItem.id} className={styles.menuColumnListItem}>
                  <Menu backgroundColor={backgroundColor} menuItem={menuItem} />
                </li>
                );
              })}
            </ul>
          </nav>
        )}
        <div className={styles.languageSelector}>
          <LanguageSelector
            backgroundColor={backgroundColor}
            currentLanguage={langCode?.toLowerCase() ?? siteMetadataLangCode.toLowerCase()}
            sites={sites}
            translations={translations}
          />
        </div>
        {isDefaultNav && (
          <>
            <div className={styles.logo}>
              <Icon color='currentColor' iconName={footerIconName} style={{ height: 'auto' }} />
            </div>
            <div className={styles.social}>
              {SOCIAL_PLATFORMS.map(({ name, url }) => (
                <UniversalMenuItem
                  backgroundColor={backgroundColor}
                  className={styles.socialLink}
                  href={url}
                  key={name}
                  rel='noopener'
                >
                  <Icon color='currentColor' size={1.6} iconName={name} />
                  <span className={styles.socialIconText}>{name}</span>
                </UniversalMenuItem>
              ))}
            </div>
          </>
        )}
        <div className={generateClassName([styles.legal, styles[backgroundColor]])}>
          <div>
            {t('organisms_footer_copyright', { year: (new Date()).getFullYear() })}
          </div>
          <nav className={styles.legalNavigation}>
            {copyrightMenuItems?.map((menuItem) => (
              <UniversalMenuItem backgroundColor={backgroundColor} key={menuItem.label} className={styles.legalLink} href={menuItem.url} label={menuItem.label} />
            ))}
            <UniversalMenuItem
              backgroundColor={backgroundColor}
              as='button'
              className={generateClassName(['ot-sdk-show-settings', styles.legalLink, styles.privacy, styles[backgroundColor]])}
              label={t('organisms_footer_privacySettings')}
              id='ot-sdk-btn'
              onClick={() => window?.OneTrust?.ToggleInfoDisplay?.()}
              suppressHydrationWarning={true}
            />
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default UniversalFooter;
